/**
 * ShoppingCart.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect } from 'react';
import { useCart } from '../hooks/useCart';
import { useAuth } from '../context/AuthContext';
import { useCustomerCurrencyContext } from '../hooks/useCustomerCountry';
import { useCurrencyRates } from '../hooks/useCurrencyRates';
import { useToast } from './ToastContainer';
import { useNavigate } from 'react-router-dom';
import { formatCartMoney, formatDualCurrency, referenceCurrencyNote, showsReferenceCurrency } from '../utils/currencyUtils';
import { isDigitalAlbum } from '../utils/shippingConfig';
import CartItem from './CartItem';
import { useLanguage } from '../context/LanguageContext';
import { fmt } from '../i18n/format';
import './ShoppingCart.css';

const ShoppingCart = ({ onClose }) => {
  const {
    items,
    clearCart,
    getCartTotal,
    getWholesaleSavings,
    getTransactionFee,
    getTotalWithShippingAndTax,
    updateShipping,
    shipping,
  } = useCart();
  const { currentUser } = useAuth();
  const currencyContext = useCustomerCurrencyContext();
  const { countryCode: customerCountry, countryName: customerCountryName } = currencyContext;
  const currencyRates = useCurrencyRates();
  const { showSuccess } = useToast();
  const { t } = useLanguage();
  const cart = t.shop.cart;
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const navigate = useNavigate();

  // Load shipping + payment processing fee % for cart summary (uses customer country default).
  // Do not depend on updateShipping identity — CartPanel is always mounted, and a new
  // function each render would recalculate shipping in a tight loop and freeze the page.
  useEffect(() => {
    if (items.length === 0) return;
    const country = customerCountry || 'CA';
    if (shipping.isCalculated && shipping.country === country && shipping.method === 'standard') {
      return;
    }
    updateShipping('standard', country, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateShipping is memoized on items
  }, [items, customerCountry, shipping.isCalculated, shipping.country, shipping.method]);

  const cartTotal = getCartTotal() || 0;
  const transactionFeeInfo = getTransactionFee();
  const transactionFee = transactionFeeInfo.amount || 0;
  const transactionFeePct = transactionFeeInfo.pct || 0;
  const estimatedTotal = getTotalWithShippingAndTax() || cartTotal;
  const wholesaleSavings = getWholesaleSavings() || 0;
  const hasWholesaleItems = items.some(item => item.isWholesalePrice);
  const hasDigitalDownload = items.some((item) => {
    if (item.isCheckoutPromo) return false;
    return isDigitalAlbum(item);
  });
  const hasPhysicalItem = items.some((item) => {
    if (item.isCheckoutPromo) return false;
    if (isDigitalAlbum(item)) return false;
    return item.shipping_enabled !== false && item.shipping_enabled !== 0;
  });
  const isMixedDigitalPhysical = hasDigitalDownload && hasPhysicalItem;
  const showDual = showsReferenceCurrency(customerCountry, customerCountryName);
  const dualCartTotal = formatDualCurrency(cartTotal, customerCountry, currencyRates, customerCountryName);
  const dualWholesaleSavings = formatDualCurrency(wholesaleSavings, customerCountry, currencyRates, customerCountryName);
  const dualTransactionFee = formatDualCurrency(transactionFee, customerCountry, currencyRates, customerCountryName);
  const dualEstimatedTotal = formatDualCurrency(estimatedTotal, customerCountry, currencyRates, customerCountryName);

  const handleCheckout = () => {
    navigate('/checkout');
    // Close the cart panel after navigating to checkout
    if (onClose) {
      onClose();
    }
  };

  const handleClearCart = () => {
    if (items.length === 0) return;
    setShowClearConfirm(true);
  };

  const confirmClearCart = () => {
    clearCart();
    setShowClearConfirm(false);
    showSuccess(cart.cleared);
  };

  const cancelClearCart = () => {
    setShowClearConfirm(false);
  };

  return (
    <div className="shopping-cart">
      <div className="cart-header">
        <h2>{cart.shoppingCart}</h2>
        {currentUser?.isWholesaler && (
          <div className="wholesale-user-badge">
            <span className="wholesale-icon">🏢</span>
            {cart.wholesaleCustomer}
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <div className="empty-cart">
          <p>{cart.empty}</p>
          {currentUser?.isWholesaler && (
            <div className="wholesale-info">
              <p>{cart.wholesaleGets}</p>
              <ul>
                <li>{cart.wholesaleBullet1}</li>
                <li>{cart.wholesaleBullet2}</li>
                <li>{cart.wholesaleBullet3}</li>
              </ul>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="cart-items">
            {items.map((item) => (
              <CartItem
                key={item.cartItemId || item.id}
                item={item}
                customerCountry={customerCountry}
                customerCountryName={customerCountryName}
              />
            ))}
          </div>
          {hasDigitalDownload && (
            <div
              className="cart-digital-note"
              style={{
                fontSize: '0.8rem',
                color: '#1e40af',
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '6px',
                padding: '8px 10px',
                margin: '0.75rem 0',
                lineHeight: 1.45,
              }}
            >
              {isMixedDigitalPhysical ? <>📥 {cart.digitalMixed}</> : <>📥 {cart.digitalOnly}</>}
            </div>
          )}
          <div className="cart-total-section">
            <div className="cart-total-row">
              <span className="cart-total-label">{cart.subtotal}</span>
              <span className="cart-total-amount">
                {showDual && dualCartTotal ? (
                  <>
                    {dualCartTotal.cad}
                    <span className="cart-ref-currency">{dualCartTotal.secondary}</span>
                  </>
                ) : (
                  formatCartMoney(cartTotal, customerCountry)
                )}
              </span>
            </div>
            {shipping.isCalculated && shipping.cost > 0 && (
              <div className="cart-total-row cart-fee-row">
                <span className="cart-total-label">{cart.estShipping}</span>
                <span className="cart-fee-amount">
                  {formatCartMoney(shipping.cost, customerCountry)}
                </span>
              </div>
            )}
            {transactionFeePct > 0 && (
              <div className="cart-total-row cart-fee-row">
                <span className="cart-total-label">
                  {fmt(cart.processingFee, { pct: transactionFeePct })}
                </span>
                <span className="cart-fee-amount">
                  {showDual && dualTransactionFee ? (
                    <>
                      {dualTransactionFee.cad}
                      <span className="cart-ref-currency">{dualTransactionFee.secondary}</span>
                    </>
                  ) : (
                    formatCartMoney(transactionFee, customerCountry)
                  )}
                </span>
              </div>
            )}
            {transactionFeePct > 0 && (
              <div className="cart-total-row cart-estimated-total-row">
                <span className="cart-total-label">{cart.estTotal}</span>
                <span className="cart-total-amount">
                  {showDual && dualEstimatedTotal ? (
                    <>
                      {dualEstimatedTotal.cad}
                      <span className="cart-ref-currency">{dualEstimatedTotal.secondary}</span>
                    </>
                  ) : (
                    formatCartMoney(estimatedTotal, customerCountry)
                  )}
                </span>
              </div>
            )}
            {transactionFeePct > 0 && (
              <p className="cart-fee-note">
                {shipping.cost > 0 ? cart.feeNoteWithShipping : cart.feeNoteNoShipping}
              </p>
            )}
            {hasWholesaleItems && wholesaleSavings > 0 && (
              <div className="cart-savings-row">
                <span className="cart-savings-label">{cart.youreSaving}</span>
                <span className="cart-savings-amount">
                  {showDual && dualWholesaleSavings ? (
                    <>
                      {dualWholesaleSavings.cad}
                      <span className="cart-ref-currency">{dualWholesaleSavings.secondary}</span>
                    </>
                  ) : (
                    formatCartMoney(wholesaleSavings, customerCountry)
                  )}
                </span>
              </div>
            )}
            {showDual && referenceCurrencyNote(customerCountry, currencyRates, customerCountryName) && (
              <p className="cart-currency-note">
                {referenceCurrencyNote(customerCountry, currencyRates, customerCountryName)}
              </p>
            )}
          </div>
          <div className="cart-actions">
            <button onClick={handleCheckout} className="checkout-btn">
              {cart.proceed}
            </button>
            {!showClearConfirm ? (
              <button onClick={handleClearCart} className="clear-cart-btn">
                {cart.clearCart}
              </button>
            ) : (
              <div className="clear-confirm">
                <span className="confirm-text">{cart.clearConfirm}</span>
                <div className="clear-confirm-buttons">
                  <button type="button" onClick={confirmClearCart} className="confirm-yes">
                    {cart.yes}
                  </button>
                  <button type="button" onClick={cancelClearCart} className="confirm-no">
                    {cart.no}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ShoppingCart; 